import App from '@/models/App'
import OrderingOrdererEstimateItem from '@/models/OrderingOrdererEstimateItem'
import OrderingOrdererEstimateExpense from '@/models/OrderingOrdererEstimateExpense'
import OrderingOrdererEstimateDiscount from '@/models/OrderingOrdererEstimateDiscount'
import OrderingOrdererOrder from '@/models/OrderingOrdererOrder'
import OrderingOrdererAccount from '@/models/OrderingOrdererAccount'
import OrderingOrdererEstimatePriceSummary from '@/models/OrderingOrdererEstimatePriceSummary'
import OrderingOrdererItemCategory from '@/models/OrderingOrdererItemCategory'
import OrderingOrdererItemSubCategory from '@/models/OrderingOrdererItemSubCategory'
import OrderingOrdererEstimateApproval from '@/models/OrderingOrdererEstimateApproval';
import ordering from '@/store/modules/ordering'

export default class OrderingOrdererEstimate extends App {
    static get entity() {
        return 'OrderingOrdererEstimates'
    }

    static all() {
        return this.query()
            .with('orderingEstimateExpenses')
            .with('orderingEstimateDiscounts')
            .with('orderingEstimatePriceSummaries')
            .with('orderingEstimateApprovals')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemImages')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemCategory')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemSubCategory')
            .with('orderingOrder.orderingAccount.user')
            .with('orderingAccount.user')
            .get()
    }

    static find(orderingEstimateId) {
        return this.query()
            .with('orderingEstimateExpenses')
            .with('orderingEstimateDiscounts')
            .with('orderingEstimatePriceSummaries')
            .with('orderingEstimateApprovals')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemImages')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemCategory')
            .with('orderingEstimateItems.orderingOrderItem.orderingItem.orderingItemSubCategory')
            .with('orderingOrder.orderingAccount.user')
            .with('orderingAccount.user')
            .where('id', orderingEstimateId)
            .first()
    }

    static fields() {
        return {
            id: this.attr(''),
            orderingOrderId: this.attr(''),
            orderingAccountId: this.attr(''),
            name: this.attr(''),
            number: this.attr(0),
            status: this.attr(''),
            possibleDeliveryDate: this.attr(''),
            isAbleToSpecifyDeliveryTime: this.attr(''),
            postage: this.attr(0),
            reasonForRemand: this.attr(''),
            approvalType: this.attr(''),
            approvalReturnAccountId: this.attr(''),
            approvalReturnMessage: this.attr(''),
            createdAt: this.attr(''),
            updatedAt: this.attr(''),
            estimateCompanyName: this.attr(''),
            orderItemCount: this.attr(0),
            orderingEstimateItems: this.hasMany(
                OrderingOrdererEstimateItem,
                'orderingEstimateId'
            ),
            orderingEstimateExpenses: this.hasMany(
                OrderingOrdererEstimateExpense,
                'orderingEstimateId'
            ),
            orderingEstimateDiscounts: this.hasMany(
                OrderingOrdererEstimateDiscount,
                'orderingEstimateId'
            ),
            orderingEstimatePriceSummaries: this.hasMany(
                OrderingOrdererEstimatePriceSummary,
                'orderingEstimateId'
            ),
            orderingOrder: this.belongsTo(
                OrderingOrdererOrder,
                'orderingOrderId'
            ),
            orderingAccount: this.belongsTo(
                OrderingOrdererAccount,
                'orderingAccountId'
            ),
            orderingEstimateApprovals: this.hasMany(
                OrderingOrdererEstimateApproval,
                'orderingEstimateId'
            ),
        }
    }

    static apiConfig = {
        actions: {
            fetch(orderingShipId, orderingOrderId, params) {
                return this.get(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates`,
                    {
                        params: App.get(params),
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        dataKey: 'data',
                        baseURL: App.apiURL(),
                    }
                )
            },
            find(orderingShipId, orderingOrderId, orderingEstimateId, params) {
                return this.get(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}`,
                    {
                        params: App.get(params),
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        dataKey: 'data',
                        baseURL: App.apiURL(),
                    }
                )
            },
            update(orderingShipId, orderingOrderId, orderingEstimateId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}`,
                    App.patch(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            updateApproval(orderingShipId, orderingOrderId, orderingEstimateId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}/approval`,
                    App.patch(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            submitOrder(
                orderingShipId,
                orderingOrderId,
                orderingEstimateId,
                params
            ) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}/submitOrder`,
                    App.patch(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            remand(
                orderingShipId,
                orderingOrderId,
                orderingEstimateId,
                params
            ) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}/remand`,
                    App.patch(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            destroy(orderingShipId, orderingOrderId, orderingEstimateId) {
                return this.delete(
                    `ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}`,
                    {
                        params: {},
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        delete: orderingEstimateId,
                        baseURL: App.apiURL(),
                    }
                )
            },
            importDetails(orderingShipId, orderingOrderId, orderingEstimateId, params) {
                return this.post(
                    `ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/estimates/${orderingEstimateId}/details/import`,
                    App.post(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
        },
    }
}
